import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Table, Form, Button, Spinner, Alert } from 'react-bootstrap';
import LoadingDots from './LoadingDots';
import { useIntl } from 'react-intl';
import getLocalizedText from './Utils';
import Config from '../config/Config';
import '../css/SubmodelRegistryView.css';

import {
  getSubmodelDescriptor,
  deleteSubmodelDescriptor,
  copyShellDescriptor,
  copySubmodelDescriptor,
  deleteShellDescriptor,
} from '../services/DppManagerService';

const SubmodelRegistryView = ({
  aasDescriptor,
  submodelDescriptors = [],
  shellAvailability = { PRIVATE: true, INTERMEDIATE: false, PUBLIC: false },
  registries = ['PRIVATE', 'INTERMEDIATE', 'PUBLIC'],
  onShellAvailabilityChange = () => {},
}) => {
  const { mainColor, secondaryColor } = Config.appearance;

  const intl = useIntl();

  const [loadingInitial, setLoadingInitial] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const initialRegistryMapRef = useRef({});

  const [currentRegistryMap, setCurrentRegistryMap] = useState(() => {
    const map = {};
    registries.forEach((r) => (map[r] = new Set()));
    return map;
  });

  const [shellPresent, setShellPresent] = useState({
    PRIVATE: true,
    INTERMEDIATE: false,
    PUBLIC: false,
  });

  const nonPrivateRegistries = useMemo(
    () => registries.filter((r) => r !== 'PRIVATE'),
    [registries]
  );

  useEffect(() => {
    let cancelled = false;

    const loadInitialState = async () => {
      if (cancelled) return;
      setLoadingInitial(true);
      setError('');

      try {
        const initMap = {};
        const currMap = {};
        const shellMap = { ...shellAvailability };

        registries.forEach((r) => {
          initMap[r] = new Set();
          currMap[r] = new Set();
        });

        submodelDescriptors.forEach((sm) => {
          initMap.PRIVATE.add(sm.id);
          currMap.PRIVATE.add(sm.id);
        });
        shellMap.PRIVATE = true;

        const checks = nonPrivateRegistries.flatMap((registry) =>
          submodelDescriptors.map((sm) => ({
            registry,
            submodelId: sm.id,
            promise: getSubmodelDescriptor(sm.id, registry).catch((err) => {
              return null;
            }),
          }))
        );

        const results = await Promise.all(checks.map((c) => c.promise));

        let resultIndex = 0;
        for (const registry of nonPrivateRegistries) {
          for (const sm of submodelDescriptors) {
            const exists = !!results[resultIndex++];
            if (exists) {
              initMap[registry].add(sm.id);
              currMap[registry].add(sm.id);
            }
          }
          if (initMap[registry].size > 0) {
            shellMap[registry] = true;
          }
        }

        if (!cancelled) {
          initialRegistryMapRef.current = initMap;
          setCurrentRegistryMap(currMap);
          setShellPresent(shellMap);
          onShellAvailabilityChange(shellMap);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load registry state', err);
          setError('Could not load registry information.');
        }
      } finally {
        if (!cancelled) setLoadingInitial(false);
      }
    };

    loadInitialState();

    return () => {
      cancelled = true;
    };
  }, [submodelDescriptors, registries]);

  const handleCheckboxToggle = (submodelId, registry, checked) => {
    if (registry === 'PRIVATE') return;

    setCurrentRegistryMap((prev) => {
      const next = { ...prev };
      for (const r of Object.keys(prev)) {
        next[r] = new Set(prev[r]);
      }
      if (checked) {
        next[registry].add(submodelId);
      } else {
        next[registry].delete(submodelId);
      }
      return next;
    });
  };

  // Check if there are any changes between initial and current state
  const hasChanges = Object.keys(currentRegistryMap).some((registry) => {
    const initialSet = initialRegistryMapRef.current[registry] || new Set();
    const currentSet = currentRegistryMap[registry] || new Set();
    return (
      initialSet.size !== currentSet.size || // size mismatch
      [...currentSet].some((id) => !initialSet.has(id)) || // added items
      [...initialSet].some((id) => !currentSet.has(id)) // removed items
    );
  });

  const handleSave = async () => {
    if (!aasDescriptor?.id) {
      setError('AAS descriptor is missing.');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      const newShellPresent = { ...shellPresent };

      for (const registry of registries) {
        if (registry === 'PRIVATE') continue;

        const initialSet = initialRegistryMapRef.current[registry] || new Set();
        const currentSet = currentRegistryMap[registry] || new Set();

        const added = [...currentSet].filter((id) => !initialSet.has(id));
        const removed = [...initialSet].filter((id) => !currentSet.has(id));

        if (added.length > 0 && !newShellPresent[registry]) {
          await copyShellDescriptor(aasDescriptor.id, 'PRIVATE', registry);
          newShellPresent[registry] = true;
        }

        for (const id of added) {
          await copySubmodelDescriptor(id, 'PRIVATE', registry);
        }

        for (const id of removed) {
          await deleteSubmodelDescriptor(id, registry);
        }
      }

      for (const registry of nonPrivateRegistries) {
        const currentCount = currentRegistryMap[registry]?.size || 0;
        const hadBefore = (initialRegistryMapRef.current[registry]?.size || 0) > 0;

        if (hadBefore && currentCount === 0) {
          await deleteShellDescriptor(aasDescriptor.id, registry);
          newShellPresent[registry] = false;
        }
      }

      const newBaseline = {};
      registries.forEach((r) => {
        newBaseline[r] = new Set(currentRegistryMap[r] || []);
      });

      submodelDescriptors.forEach((sm) => newBaseline.PRIVATE.add(sm.id));

      initialRegistryMapRef.current = newBaseline;
      setShellPresent(newShellPresent);
      onShellAvailabilityChange(newShellPresent);
    } catch (err) {
      console.error('Save failed', err);
      setError('Failed to save changes. Check console for details.');
    } finally {
      setIsSaving(false);
    }
  };

  const renderBody = () => {
    if (loadingInitial) {
      return (
        <div className="text-center p-5">
          <LoadingDots size={14} color={mainColor} />
        </div>
      );
    }

    if (submodelDescriptors.length === 0) {
      return <div className="p-3 text-muted">No submodels available for this AAS.</div>;
    }

    return (
      <>
        <Table responsive>
          <thead>
            <tr>
              <th style={{ minWidth: '280px' }}>{intl.formatMessage({ id: 'submodels' })}</th>
              {registries.map((r) => (
                <th key={r} className="text-center">
                  {r}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {submodelDescriptors.map((sm) => {
              const { id } = sm;

              const displayName = getLocalizedText(
                sm.displayName,
                sm.idShort || id || 'Unnamed',
                intl.locale
              );

              const description = getLocalizedText(
                sm.description,
                '-',
                intl.locale
              );

              return (
                <tr key={id}>
                  <td style={{ minWidth: '280px' }}>
                    <div>
                      <strong>{displayName}</strong>
                      <br />
                      <>{description}</>
                      <br />
                      <code>
                        SM-ID: {id}
                      </code>
                    </div>
                  </td>

                  {registries.map((registry) => {
                    const checked = currentRegistryMap[registry]?.has(id) ?? false;
                    const disabled = registry === 'PRIVATE';

                    return (
                      <td key={registry} className="text-center">
                        <Form.Check
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={(e) =>
                            handleCheckboxToggle(id, registry, e.target.checked)
                          }
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
        <div className="d-flex justify-content-end mt-4">
          <Button
            variant="success"
            onClick={handleSave}
            disabled={isSaving || loadingInitial || !hasChanges}
            className="ms-2"
          >
            {isSaving ? (
              intl.formatMessage({ id: 'saving' })
            ) : (
              intl.formatMessage({ id: 'save_changes' })
            )}
          </Button>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}
      </>
    );
  };

  return <div 
    className="submodel-registry" 
    style={{ 
      '--dpp-main-color': mainColor, 
      '--dpp-secondary-color': secondaryColor
    }}>{renderBody()}
    </div>;
};

export default SubmodelRegistryView;
